import { Link } from "react-router-dom";
import { formatBoon, formatUsdc, shortAddr } from "../../lib/api";
import { baseAddressFromAccountId, type X402ProfileActivity } from "../../lib/x402Profile";

export function X402ActivityRow({ fact, compact = false }: { fact: X402ProfileActivity; compact?: boolean }) {
  const address = baseAddressFromAccountId(fact.accountId);
  const transactionUrl = safeBaseTransactionUrl(fact.transaction);
  const summary = activitySummary(fact);
  const attachedReview = attachedReviewText(fact);
  return (
    <li className={compact ? "py-4" : "py-5"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="pill pill-olive btn-mono text-[10px]">{activityLabel(fact.kind)}</span>
            <span className="pill pill-faint btn-mono text-[10px]">{evidenceLabel(fact.evidenceKind)}</span>
            {attachedReview && fact.kind !== "reviewed" && (
              <span className="pill pill-faint btn-mono text-[10px]">signed subjective review</span>
            )}
          </div>
          <p className="mt-3 text-sm text-ink leading-relaxed break-words">{summary}</p>
          {attachedReview && fact.kind !== "reviewed" && (
            <div className="mt-3 border-l-2 border-olive/30 pl-3">
              <p className="text-sm leading-relaxed text-ink break-words">“{attachedReview}”</p>
              <p className="mt-1 btn-mono text-[10px] uppercase tracking-[0.12em] text-muted">
                Verified-use-backed subjective review
              </p>
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            {fact.routeId && (
              <Link className="btn-mono text-xs text-olive-deep hover:underline" to={`/x402/routes/${fact.routeId}`}>
                View endpoint route →
              </Link>
            )}
            {transactionUrl && (
              <a className="btn-mono text-xs text-olive-deep hover:underline" href={transactionUrl} target="_blank" rel="noopener noreferrer">
                View transaction ↗
              </a>
            )}
          </div>
          <p className="mt-2 btn-mono text-xs text-muted">
            {address ? shortAddr(address) : fact.accountId} · {formatDate(fact.occurredAt)}
          </p>
        </div>
      </div>
    </li>
  );
}

function activitySummary(fact: X402ProfileActivity): string {
  const endpoint = [fact.method, `${fact.origin ?? ""}${fact.path ?? ""}`.trim()].filter(Boolean).join(" ");
  const details = asRecord(fact.details);

  if (fact.kind === "reviewed") {
    const review = asRecord(details?.review);
    const reviewText = readString(review?.reviewText);
    return reviewText ? `“${reviewText}”` : endpoint || "Published a review of this endpoint.";
  }

  if (fact.kind === "recognized") {
    const endpointGratuity = readString(details?.endpointGratuityUsdc);
    const networkGratuity = readString(details?.networkGratuityUsdc);
    const totalRecognition = addAtomic(endpointGratuity, networkGratuity);
    const boonBurned = readString(details?.boonBurned);
    const recognition = totalRecognition
      ? `${formatUsdc(totalRecognition)} total recognition${isPositiveAtomic(networkGratuity) ? ` (${formatUsdc(endpointGratuity)} endpoint + ${formatUsdc(networkGratuity)} network)` : ""}`
      : null;
    const facts = [recognition, boonBurned ? `${formatBoon(boonBurned)} burned` : null].filter(Boolean);
    return facts.length > 0 ? facts.join(" · ") : endpoint || "Sent a routed Boon to this endpoint.";
  }

  if (fact.kind === "paid_settlement") {
    const amount = fact.amountAtomic ? `${formatUsdc(fact.amountAtomic)} settled` : "Published a paid x402 settlement.";
    return endpoint ? `${endpoint} · ${amount}` : amount;
  }

  const settlement = fact.amountAtomic ? `${formatUsdc(fact.amountAtomic)} settlement` : null;
  if (endpoint && settlement) return `${endpoint} · ${settlement}`;
  return endpoint || settlement || "Published a wallet-attested x402 route.";
}

function attachedReviewText(fact: X402ProfileActivity): string | null {
  const details = asRecord(fact.details);
  const review = asRecord(details?.review);
  return readString(review?.reviewText);
}

function activityLabel(kind: X402ProfileActivity["kind"]): string {
  if (kind === "paid_settlement") return "Settled";
  return `${kind[0]?.toUpperCase() ?? ""}${kind.slice(1)}`;
}

function evidenceLabel(kind: X402ProfileActivity["evidenceKind"]): string {
  if (kind === "client_settlement") return "settlement-verified use";
  if (kind === "receipt_settlement") return "provider receipt + settlement";
  if (kind === "boon_backed") return "Boon-backed";
  if (kind === "routed_boon") return "routed Boon";
  return kind.replaceAll("_", " ");
}

function isPositiveAtomic(value: string | null): boolean {
  try {
    return value !== null && BigInt(value) > 0n;
  } catch {
    return false;
  }
}

function addAtomic(left: string | null, right: string | null): string | null {
  if (!left && !right) return null;
  try {
    return (BigInt(left ?? "0") + BigInt(right ?? "0")).toString();
  } catch {
    return left ?? right;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function safeBaseTransactionUrl(value: string | null | undefined): string | null {
  return value && /^0x[0-9a-fA-F]{64}$/.test(value) ? `https://basescan.org/tx/${value}` : null;
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1_000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
